import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import {
  Button,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import proces from "../images/proces.png";
import { UserDataContext } from "../components/UserDataContext";
import VN from "../components/VN";

// Ikony
import LoginIcon from "@mui/icons-material/Login";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import NorthIcon from "@mui/icons-material/North";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Face3OutlinedIcon from "@mui/icons-material/Face3Outlined";
import Person3OutlinedIcon from "@mui/icons-material/Person3Outlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AccountTreeIcon from "@mui/icons-material/AccountTree"; // Nowa ikona dla procesu

export default function AppDescription() {
  const { userData } = useContext(UserDataContext);
  const navigate = useNavigate();

  // Nowoczesne style w jednym obiekcie
  const styles = {
    sectionPaper: {
      padding: "30px",
      borderRadius: "16px",
      marginBottom: "40px",
      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    mainButton: {
      marginBottom: "20px",
      width: "100%",
      maxWidth: "360px",
      justifyContent: "flex-start",
      padding: "12px 24px",
      borderRadius: "12px",
      textTransform: "none",
      fontWeight: "600",
      fontSize: "1rem",
      gap: "15px",
    },
    checkIcon: {
      color: "#2e7d32",
    },
    goUpBtn: {
      minWidth: "40px",
      padding: "8px",
      borderRadius: "50%",
      marginLeft: "10px",
    },
  };

  const UpButton = () => (
    <Button
      variant="text"
      href="#start"
      style={styles.goUpBtn}
      title="Skocz do góry"
    >
      <NorthIcon fontSize="small" sx={{ color: "#757575" }} />
    </Button>
  );

  return (
    <div
      style={{
        scrollBehavior: "smooth",
        backgroundColor: "#fafafa",
        padding: "40px 0",
      }}
    >
      <VN userData={userData} />

      <Container className="page-content" style={{ maxWidth: "900px" }}>
        {/* === SEKCJA 1: CEL DZIAŁANIA === */}
        <Paper style={styles.sectionPaper} elevation={0}>
          <Button
            variant="contained"
            color="primary"
            style={styles.mainButton}
            href="#cel-sekcja"
            startIcon={<InfoOutlinedIcon />}
          >
            Cel działania aplikacji
          </Button>

          <Box id="cel-sekcja" pt={2} sx={{ scrollMarginTop: "90px" }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#1a237e" }}
            >
              Bezpieczeństwo i pewność wyboru
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: "#555", lineHeight: "1.7" }}
            >
              Celem działania aplikacji jest połączenie osób szukających opieki
              ze zweryfikowaną osobą, posiadającą odpowiednie kwalifikacje i
              zweryfikowaną przez uprawnioną pielęgniarkę.
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: "#555", lineHeight: "1.7", fontWeight: "500" }}
            >
              Każda opiekunka jest dokładnie sprawdzana. To, co nas wyróżnia, to
              rzetelna weryfikacja każdej osoby, zanim pojawi się w serwisie.
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", mt: 3, mb: 1 }}
            >
              Sprawdzamy między innymi:
            </Typography>

            <GridCheckList
              items={[
                "Tożsamość użytkownika",
                "Doświadczenie w opiece nad seniorami",
                "Referencje od poprzednich rodzin",
                "Kwalifikacje oraz odbyte kursy",
                "Prowadzimy indywidualną rozmowę weryfikacyjną",
              ]}
              iconStyle={styles.checkIcon}
            />

            <Box
              mt={3}
              p={2}
              sx={{
                backgroundColor: "#e8f5e9",
                borderRadius: "8px",
                borderLeft: "4px solid #2e7d32",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#1b5e20", fontWeight: "500" }}
              >
                Dzięki temu masz pewność, że korzystasz tylko ze sprawdzonych
                opiekunek.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* === SEKCJA 2: DOSTĘP I REJESTRACJA OPIEKUNKI === */}
        <Paper style={styles.sectionPaper} elevation={0}>
          <Button
            variant="contained"
            color="secondary"
            style={styles.mainButton}
            href="#dostep-sekcja"
            startIcon={<LoginIcon />}
          >
            Dostęp do systemu
          </Button>

          <Box
            id="dostep-sekcja"
            pt={2}
            mb={4}
            sx={{ scrollMarginTop: "90px" }}
          >
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              Kto może korzystać z serwisu?
            </Typography>
            <Typography variant="body1" sx={{ color: "#555" }}>
              Do serwisu mają dostęp 3 dedykowane grupy użytkowników:
            </Typography>
            <List>
              {[
                "Opiekunki",
                "Osoby poszukujące opieki dla bliskich",
                "Personel medyczny weryfikujący uprawnienia",
              ].map((text, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: "32px" }}>
                    <StarBorderIcon color="secondary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />
          <Button
            variant="outlined"
            color="primary"
            style={styles.mainButton}
            href="#rejestracja-opiekunki"
            startIcon={<AppRegistrationIcon />}
          >
            Rejestracja opiekunki
          </Button>

          <Box
            id="rejestracja-opiekunki"
            pt={2}
            mb={4}
            sx={{ scrollMarginTop: "90px" }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Jak dołączyć jako Opiekunka?
            </Typography>
            <Typography variant="body2" paragraph>
              Rejestracja opiekunki następuje poprzez wybranie przycisku{" "}
              <strong>"Dołącz jako opiekunka"</strong> i wypełnienie pól
              formularza.
            </Typography>
            <Box
              p={2}
              mb={2}
              sx={{
                backgroundColor: "#fffde7",
                borderRadius: "8px",
                borderLeft: "4px solid #fbc02d",
              }}
            >
              <Typography variant="body2">
                <strong>Wskazówka dotycząca Konta:</strong> Należy utworzyć
                unikalny identyfikator (np. pierwsza litera imienia i nazwisko,
                np. <em>anowak</em>). Jeśli nazwa jest zajęta, system podpowie
                dodanie cyfry, np. <em>anowak1</em>.
              </Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              Wymagane dane w formularzu:
            </Typography>
            <Row>
              <Col md={6}>
                <ul
                  style={{
                    paddingLeft: "20px",
                    fontSize: "0.9rem",
                    color: "#444",
                  }}
                >
                  <li>
                    <strong>Email:</strong> weryfikowany w bazie danych
                  </li>
                  <li>
                    <strong>Nazwisko i imię:</strong> do kontaktów
                  </li>
                  <li>
                    <strong>Telefon:</strong> weryfikowany kodem SMS
                  </li>
                  <li>
                    <strong>Rok urodzenia & Obywatelstwo</strong>
                  </li>
                </ul>
              </Col>
              <Col md={6}>
                <ul
                  style={{
                    paddingLeft: "20px",
                    fontSize: "0.9rem",
                    color: "#444",
                  }}
                >
                  <li>
                    <strong>Hasło:</strong> zabezpieczenie profilu
                  </li>
                  <li>
                    <strong>Województwo i Miasto:</strong> kluczowe do
                    lokalizacji
                  </li>
                </ul>
              </Col>
            </Row>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Profil Opiekunki */}
          <Button
            variant="outlined"
            color="success"
            style={styles.mainButton}
            href="#profil-opiekunki"
            startIcon={<Face3OutlinedIcon />}
          >
            Profil Opiekunki <br />
            (Po zalogowaniu)
          </Button>

          <Box id="profil-opiekunki" pt={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Kroki uzupełniania profilu opiekunki:
            </Typography>

            <List dense>
              {[
                {
                  title: "1. Dane personalne",
                  desc: "Weryfikacja e-mail kodem oraz opcja dodania zdjęcia profilowego.",
                },
                {
                  title: "2. Zakres obowiązków (Co mogę zrobić?)",
                  desc: "Zaznaczenie czynności (toaleta, podawanie leków, pielęgnacja itp.).",
                },
                {
                  title: "3. Obszar pracy (Gdzie mogę pracować?)",
                  desc: "Wybór miejscowości i województw, w których możesz podjąć zatrudnienie.",
                },
                {
                  title: "4. Kwalifikacje i uprawnienia",
                  desc: "Kursy opiekuna, wykształcenie medyczne, pierwsza pomoc.",
                },
                {
                  title: "5. Doświadczenie specjalistyczne",
                  desc: "Opieka nad chorymi na Alzheimer, demencję, Parkinson czy opieka paliatywna.",
                },
                {
                  title: "6. Języki obce",
                  desc: "Określenie stopnia znajomości języków.",
                },
                {
                  title: "7. Dodatkowe informacje i weryfikacja",
                  desc: "Wpisanie uwag własnych i kliknięcie 'Zakończ edycję i prześlij do weryfikacji' (profil zostanie zablokowany do czasu sprawdzenia przez koordynatora).",
                },
              ].map((item, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemText
                    primary={<strong>{item.title}</strong>}
                    secondary={item.desc}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>

        {/* === SEKCJA 3: REJESTRACJA RODZINY === */}
        <Paper style={styles.sectionPaper} elevation={0}>
          <Button
            variant="contained"
            color="info"
            style={styles.mainButton}
            href="#rejestracja-szukajacego"
            startIcon={<Person3OutlinedIcon />}
          >
            Rejestracja osoby poszukującej
          </Button>

          <Box id="rejestracja-szukajacego" pt={2}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#006064" }}
            >
              Szukasz opiekunki dla bliskiego?
            </Typography>
            <Typography variant="body1" paragraph>
              Wybierz przycisk{" "}
              <strong>"Dołącz jako szukający opiekunki"</strong>. Twoim loginem
              stanie się adres e-mail, który automatycznie zweryfikujemy
              bezpiecznym jednorazowym kodem.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Podczas rejestracji poprosimy Cię o podanie: Imienia i nazwiska,
              numeru telefonu, bezpiecznego hasła oraz województwa i miasta, w
              którym docelowo ma być sprawowana opieka.
            </Typography>
          </Box>
        </Paper>

        {/* === NOWA SEKCJA 4: SCHEMAT DZIAŁANIA / PROCES WSPÓŁPRACY === */}
        <Paper style={styles.sectionPaper} elevation={0}>
          <Button
            variant="contained"
            color="warning" // Ładny, ciepły kolor pomarańczowo-żółty wyróżniający proces krok po kroku
            style={styles.mainButton}
            href="#proces-wspolpracy"
            startIcon={<AccountTreeIcon />}
            sx={{ color: "#fff", "&:hover": { backgroundColor: "#e65100" } }} // Dobre kontrasty dla czytelności tekstu
          >
            Proces współpracy
          </Button>

          <Box id="proces-wspolpracy" pt={2} sx={{ scrollMarginTop: "90px" }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#e65100", mb: 2 }}
            >
              Jak działa nasz system krok po kroku?
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "#555", mb: 3 }}>
              Poniższy schemat ilustruje przejrzystą ścieżkę weryfikacji i
              kontaktu. Dbamy o to, aby cały proces od rejestracji do
              znalezienia idealnej opieki przebiegał sprawnie i w bezpiecznych
              warunkach.
            </Typography>

            {/* Kontener na obrazek z ładnym obramowaniem i delikatnym cieniem */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 1,
                backgroundColor: "#fff",
                border: "1px solid #eaeaea",
                borderRadius: "12px",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.02)",
              }}
            >
              <img
                src={proces}
                alt="Schemat procesu współpracy"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Container>
    </div>
  );
}

function GridCheckList({ items, iconStyle }) {
  return (
    <Row className="mt-2 mb-2">
      {items.map((text, i) => (
        <Col xs={12} sm={6} key={i} className="d-flex align-items-center mb-2">
          <CheckOutlinedIcon style={iconStyle} sx={{ marginRight: "10px" }} />
          <Typography variant="body2" sx={{ color: "#444" }}>
            {text}
          </Typography>
        </Col>
      ))}
    </Row>
  );
}
